<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>アクセス回線一覧</span>
        <manual-button path="/access-line-overview/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="searchOptions" v-model="accessLine.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn
              v-permission.admin
              color="primary"
              @click="showModificationModal"
              >契約変更</v-btn
            >
            <v-btn :href="downloadUrl" color="primary">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="accessLine.sortBy"
          primary-key="lineSeq"
          :selectable="selectable"
          :fields="displayFields"
          :items="accessLine.items"
          :filterOption="accessLine.filter_option"
          :filter="accessLine.filter"
          v-model="accessLine.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="showDetails"
        >
          <!-- 帯域固定 -->
          <template #cell(fixingBandwidth)="data">
            {{ $filter.emptyTo(data.value, "-") }}
          </template>

          <!-- QoS -->
          <template #cell(qos)="data">
            {{ $filter.emptyTo(data.value, "-") }}
          </template>

          <!-- vlanControl -->
          <template #cell(vlanControl)="data">
            {{ $filter.emptyTo(data.value, "-") }}
          </template>

          <!-- office -->
          <template #cell(office)="data">
            {{ $filter.emptyTo(data.value, "-") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  AccessLineGetEntity,
  BandwidthListGetEntity,
} from "@/apis/AccessLineApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { ContractListEntity } from "@/apis/ContractLineApi";
import AccessLineReference from "@/modals/accessLine/AccessLineReference.vue";
import AccessLineModify from "@/modals/accessLine/AccessLineModify.vue";

export default defineComponent({
  name: "AccessLineList",
  data() {
    return {
      alertMsg: null as string | null,
      isLoaded: false,
      accessInfo: {} as AccessLineGetEntity,
      contractList: [] as ContractListEntity[],
      accessLine: {
        sortBy: {
          key: "enumberAct",
          order: "asc",
        } as SortItem,
        fields: [
          {
            key: "enumberAct",
            title: "回線番号",
            width: "120px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              value: string,
              key: null,
              item: BandwidthListGetEntity
            ) => {
              if (item.menu === "PF_ACCESS_GW2" && item.enumberSby) {
                return `${item.enumberAct}\n${item.enumberSby}`;
              } else {
                return item.enumberAct;
              }
            },
          },
          {
            key: "menu",
            title: "メニュー",
            width: "250px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "accessLineMenu"),
          },
          {
            key: "bandwidthType",
            title: "帯域タイプ",
            width: "180px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "accessLineBandwidthType"),
          },
          {
            key: "fixingBandwidth",
            title: "帯域固定",
            width: "103px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              value: string,
              key: null,
              item: BandwidthListGetEntity
            ) => {
              if (item.menu === "ETHERNET2" || item.menu === "ETHERNET2_PF") {
                return value;
              } else {
                // メニューが「イーサネット方式Ⅱ」「イーサネット方式Ⅱ PF」でない場合
                return null;
              }
            },
          },
          {
            key: "bandwidth",
            title: "帯域値",
            width: "100px",
            sortable: true,
            sortByFormatted: true,
            formatter: (
              value: string,
              key: null,
              item: BandwidthListGetEntity
            ) => {
              if (item.bandwidthType === "BURST10") {
                return "バースト10";
              } else if (item.bandwidthType === "BURST100") {
                return "バースト100";
              } else if (item.bandwidthType === "BESTEFFORT") {
                return "-";
              } else if (item.bandwidth) {
                return this.$filter.bandwidthTo(item.bandwidth, "Mbps");
              } else {
                return "-";
              }
            },
          },
          {
            key: "qos",
            title: "QoS",
            width: "50px",
            sortable: true,
          },
          {
            key: "vlanControl",
            title: "VLAN制御",
            width: "50px",
            sortable: true,
          },
          {
            key: "office",
            title: "事業所名",
            sortable: true,
          },
        ] as AppTableHeader<BandwidthListGetEntity>[],
        items: [] as BandwidthListGetEntity[],
        selected: [] as BandwidthListGetEntity[],
        search: {
          enumberAct: { type: "text", label: "回線番号" },
          menu: {
            type: "select",
            label: "メニュー",
            items: [
              {
                value: "PF_ACCESS_GW",
                text: "PFアクセスGW",
              },
              {
                value: "EXTEND_ETHERNET",
                text: "エクステンドイーサ",
              },
              {
                value: "ETHERNET2",
                text: "イーサネット方式Ⅱ",
              },
              {
                value: "ETHERNET2_PF",
                text: "イーサネット方式Ⅱ PF",
              },
              {
                value: "BROADBAND_ACCESS3",
                text: "ブロードバンドアクセス方式Ⅲ",
              },
              {
                value: "ETHERNET_LIGHT",
                text: "イーサネットライト",
              },
              {
                value: "WIRELESS_ACCESS2",
                text: "ワイヤレスアクセス方式Ⅱ",
              },
              {
                value: "PF_ACCESS_GW2",
                text: "PFアクセスGWⅡ",
              },
              {
                value: "MULTI_CLOUD_GW",
                text: "マルチクラウドGW",
              },
            ],
          },
          bandwidthType: {
            type: "select",
            label: "帯域タイプ",
            items: [
              {
                value: "帯域確保",
                text: "帯域確保",
              },
              {
                value: "トラフィックフリー",
                text: "トラフィックフリー",
              },
              {
                value: "バースト",
                text: "バースト",
              },
              {
                value: "ベストエフォート",
                text: "ベストエフォート",
              },
            ],
          },
          fixingBandwidth: {
            type: "select",
            label: "帯域固定",
            items: [
              { value: "ON", text: "ON" },
              { value: "OFF", text: "OFF" },
              { value: null, text: "-" },
            ],
          },
          qos: {
            type: "select",
            label: "QoS",
            items: [
              { value: "ON", text: "ON" },
              { value: "OFF", text: "OFF" },
              { value: null, text: "-" },
            ],
          },
          vlanControl: {
            type: "select",
            label: "VLAN制御",
            items: [
              { value: "ON", text: "ON" },
              { value: "OFF", text: "OFF" },
              { value: null, text: "-" },
            ],
          },
          office: { type: "text", label: "事業所名" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumberAct: "like",
          menu: "equal",
          bandwidthType: "equal",
          fixingBandwidth: "equal",
          qos: "equal",
          vlanControl: "equal",
          office: "like",
        } as FilterOption,
      },
      downloadUrl: this.$api.accessLine.getCsvURL(),
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("ADMIN");
    },
    /** 法人ユーザーの場合、true */
    isNormalUser(): boolean {
      return this.$service.permission.isNormalUser();
    },
    /** 一覧の検索項目 */
    searchOptions(): SearchOption {
      if (this.isNormalUser) {
        /** 法人権限で帯域固定非表示処理 */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { fixingBandwidth, ...newSearchOptions } = this.accessLine.search;
        return newSearchOptions;
      }
      return this.accessLine.search;
    },
    /** 一覧の表示項目 */
    displayFields() {
      if (this.isNormalUser) {
        /** 法人権限で帯域固定非表示処理 */
        return this.accessLine.fields.filter(
          (v) => (v as { key: string }).key !== "fixingBandwidth"
        );
      }
      return this.accessLine.fields;
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** 一覧を取得 */
    async load() {
      this.alertMsg = null;
      this.accessInfo = await this.$api.accessLine.getAll();
      this.accessLine.items = this.accessInfo.bandwidthList.map((e) => {
        e.bandwidth =
          e.bandwidth && e.bandwidth.unit === "KBPS"
            ? this.$service.bandwidth.convertUnit(e.bandwidth, "MBPS")
            : e.bandwidth;
        return e;
      });
      //契約回線情報取得APIを呼ぶ
      this.contractList = (await this.$api.contractLine.getAll()).contractList;
    },
    /** アクセス回線変更画面表示 */
    async showModificationModal() {
      if (this.accessLine.selected.length > 0) {
        this.alertMsg = null;
        await this.$modal.show(AccessLineModify, {
          accessList: this.accessLine.selected,
          bandwidthKeyId: this.accessInfo.bandwidthKeyId,
        });
        await this.load();
      } else {
        this.alertMsg = this.$filter.message("no_select_data");
      }
    },
    /** アクセス回線情報画面表示 */
    async showDetails(item: BandwidthListGetEntity) {
      this.alertMsg = null;
      await this.$modal.show(AccessLineReference, {
        accessLineItem: item,
        // 契約リストに存在するenumberはサービス開始済とする
        isServiceStarted: this.contractList.some(
          (v) => v.enumber === item.enumberAct
        ),
      });
    },
  },
});
</script>
